<template>
  <view-container>
    <browse-tabs :model-value="BROWSE_TABS.GUIDES" view />

    <base-three-quarters-column col-spacing-y="0">
      <template #[statsSlotName]>
        <div
          class="tw-flex tw-flex-col tw-gap-y-8"
          :class="{ 'lg:tw-mt-[6.875rem]': !Settings.nameRightSide }"
        >
          <div v-if="Settings.nameRightSide" class="tw-hidden lg:tw-block">
            <h3 class="tw-leading-tight tw-text-h3 tw-font-medium tw-mb-2">
              {{ guide?.name || '&nbsp;' }}
            </h3>
            <template v-if="guide">
              <p class="tw-opacity-70 tw-leading-none tw-mb-1">
                {{ $t('published') }}
                <base-time-tooltip :timestamp="guide.date_live">{{
                  humanDateFormat(guide.date_live)
                }}</base-time-tooltip>
              </p>
              <p class="tw-opacity-70 tw-leading-none">
                {{ $t('updated') }}
                <base-time-tooltip
                  :timestamp="guide.date_updated || guide.date_live"
                >
                  {{ timeSince(guide.date_updated || guide.date_live) }}
                  {{ $t('ago') }}
                </base-time-tooltip>
              </p>
            </template>
          </div>

          <view-guide-stats :panel-loading="isLoading" :guide="guide" />

          <view-author
            class="tw-hidden lg:tw-block"
            :panel-loading="isLoading"
            :user="guide?.submitted_by"
          />

          <table-of-contents
            :guide="guide"
            :loading="isLoading"
            :comments-enabled="showComments"
            :expandable="!lgAndUp"
          />

          <view-tags
            v-if="tags.length && !isLoading"
            :class="Classes.TAGS"
            no-margin
            :tags="tags"
            @tag:filter="(input) => tagFilter(input)"
          />
        </div>
      </template>

      <template #first>
        <view-header-breadcrumbs :guide="guide" :item-type="ITEM_TYPES.GUIDE" />
        <div
          class="tw-mb-5"
          :class="{ 'lg:tw-hidden': Settings.nameRightSide }"
        >
          <h3 class="tw-leading-tight tw-text-h2 tw-font-medium">
            {{ guide?.name || '&nbsp;' }}
          </h3>
          <p v-if="guide" class="tw-opacity-70">
            {{ $t('published') }}
            <base-time-tooltip :timestamp="guide.date_live">{{
              humanDateFormat(guide.date_live)
            }}</base-time-tooltip>
            &bull;
            {{ $t('updated') }}
            <base-time-tooltip
              :timestamp="guide.date_updated || guide.date_live"
            >
              {{ timeSince(guide.date_updated || guide.date_live) }}
              {{ $t('ago') }}
            </base-time-tooltip>
          </p>
        </div>
      </template>
      <template #first-bottom>
        <div
          class="tw-my-8 lg:tw-mt-0"
          :class="[Classes.DESCRIPTION, sectionClasses]"
        >
          <view-description
            :text="descriptionWithHeadings"
            :panel-loading="isLoading"
          />
        </div>

        <view-author
          class="lg:tw-hidden tw-mb-8"
          :panel-loading="isLoading"
          :user="guide?.submitted_by"
        />

        <div
          v-if="guide && showComments"
          :class="[Classes.COMMENTS, sectionClasses]"
        >
          <discussion-panel
            :type="DISCUSSION_TYPE_GUIDES"
            :resource="guide"
            :panel-loading="isLoading"
            :game-name-id="game?.name_id"
          />
        </div>
      </template>
    </base-three-quarters-column>
  </view-container>
</template>

<script>
import ViewHeaderBreadcrumbs from '@components/View/ViewHeaderBreadcrumbs.vue'
import DiscussionPanel from '@components/Discussion/DiscussionPanel.vue'
import TableOfContents from '@components/Guide/TableOfContents.vue'
import ViewDescription from '@components/Guide/ViewDescription.vue'
import ViewGuideStats from '@components/Guide/ViewGuideStats.vue'
import ViewContainer from '@components/View/ViewContainer.vue'
import { humanDateFormat, timeSince } from '@helpers/utils.js'
import BrowseTabs from '@components/Navigation/BrowseTabs.vue'
import { GUIDE_BROWSE_ROUTE } from '@config/routeNames.js'
import { GUIDE_FILTERS_TO_HIDE } from '@config/guides.js'
import ViewAuthor from '@components/Guide/ViewAuthor.vue'
import { HEADER_ID } from '@composables/useGuideNav.js'
import { onBeforeMount, computed, provide } from 'vue'
import ViewTags from '@components/View/ViewTags.vue'
import { Settings, GAME_ID } from '@config/game.js'
import { guideStore, gameStore } from '@stores'
import Classes from '@shared/js/config/css.js'
import { useBreakpoints } from '@vueuse/core'
import { getGuideRequest } from '@services'
import { BREAKPOINTS } from '@config'
import {
  allowDownvoteGuideComments,
  allowGameGuideComments,
  allowGuideComments,
} from '@helpers/bitHelper.js'
import {
  DISCUSSION_TYPE_GUIDES,
  STATUS_ACCEPTED,
  BROWSE_TABS,
  ITEM_TYPES,
} from '@config/options.js'
import {
  useCommentAuthors,
  useGuideNav,
  useAsync,
  useRoute,
} from '@composables'

export default {
  components: {
    ViewHeaderBreadcrumbs,
    DiscussionPanel,
    ViewDescription,
    TableOfContents,
    ViewGuideStats,
    ViewContainer,
    BrowseTabs,
    ViewAuthor,
    ViewTags,
  },
  setup() {
    const { getGuideId, push, routerHistory } = useRoute()

    const discussionSlotName = computed(() =>
      lgAndUp.value ? 'first-bottom' : 'second-bottom'
    )

    const guideNameId = getGuideId().value

    const sectionClasses =
      'game-tile-background-color tw-p-4 game-tile-radius game-tile-border'

    const { greaterOrEqual } = useBreakpoints(BREAKPOINTS)
    const { addGuide, getGuide } = guideStore()
    const { setAuthors } = useCommentAuthors()
    const { getPublicGame } = gameStore()
    const lgAndUp = greaterOrEqual('lg')
    const game = getPublicGame()

    const gameAllowDownvote = computed(() =>
      allowDownvoteGuideComments(game.value.community_options)
    )

    provide('gameAllowDownvote', gameAllowDownvote)

    const showComments = computed(
      () =>
        Settings.value.showComments &&
        allowGameGuideComments(game.value.community_options) &&
        allowGuideComments(guide.value?.community_options)
    )

    const guide = getGuide(GAME_ID, guideNameId)

    const {
      run: runFetchGuide,
      data: guideData,
      loading: guideLoading,
      error: guideErrors,
    } = useAsync(() => getGuideRequest(guideNameId))

    const guideImage = computed(() => guide.value?.logo && [guide.value.logo])
    const hasDescription = computed(() => guide.value?.description)
    const isLoading = computed(() => guideLoading.value)
    const guideHidden = computed(
      () => guide.value && guide.value.status !== STATUS_ACCEPTED
    )
    const statsSlotName = computed(() =>
      lgAndUp.value ? 'second' : 'first-middle'
    )

    const tags = computed(
      () =>
        guide.value?.tags.filter(
          (t) => !GUIDE_FILTERS_TO_HIDE.includes(t.name)
        ) || []
    )

    const { insertHeadingIds } = useGuideNav(guide, null)

    const descriptionWithHeadings = computed(() =>
      hasDescription.value ? insertHeadingIds(guide.value.description) : ''
    )

    onBeforeMount(() => {
      if (!guide.value) {
        fetchGuide()
      } else {
        setAuthors([
          guide.value.submitted_by.name_id || guide.value.submitted_by.username,
        ])
      }
    })

    async function fetchGuide() {
      await runFetchGuide()

      if (!guideErrors.value) {
        addGuide(GAME_ID, guideData.value)
        setAuthors([
          guide.value.submitted_by.name_id || guide.value.submitted_by.username,
        ])
      }
    }

    function tagFilter(name) {
      push({
        name: GUIDE_BROWSE_ROUTE,
        query: { tags: name },
      })
    }

    function goBack() {
      if (routerHistory.value.back) {
        history.back()
      } else {
        push({ name: GUIDE_BROWSE_ROUTE })
      }
    }

    return {
      descriptionWithHeadings,
      DISCUSSION_TYPE_GUIDES,
      GUIDE_BROWSE_ROUTE,
      discussionSlotName,
      humanDateFormat,
      sectionClasses,
      statsSlotName,
      showComments,
      BROWSE_TABS,
      guideHidden,
      guideImage,
      ITEM_TYPES,
      timeSince,
      isLoading,
      HEADER_ID,
      tagFilter,
      Settings,
      lgAndUp,
      Classes,
      goBack,
      guide,
      tags,
      game,
    }
  },
}
</script>

<style scoped lang="css">
@media screen and (min-width: 1024px) {
  .stats {
    max-height: calc(100vh - 5.5rem - 6.25rem);
    max-height: calc(100dvh - 5.5rem - 6.25rem);
  }
}
</style>
